import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Output
} from '@angular/core';
import {CoreActionCreatorService} from '../../../core/services/core-action-creator.service';
import {DateHandler} from '../../../shared/utils/DateHandler';
import {arrToQueryString} from '../../../core/utils/query.util';
import {isEmpty} from 'lodash';
import {StringUtils} from '@ispm/shared/utils/String.utils';
import {ActivityService} from '@ispm/user/services/activity.service';
import {NotesService} from '@ispm/user/services/notes.service';
import {ActivityActions} from "@ispm/user/constants/activityActions";

const defaultAvatarUrl = "/assets/img/wallpaper-for-facebook-profile-photo.jpg";


@Component({
  selector: 'gen-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotesComponent implements OnInit, OnDestroy {

  @Input() actorType: any;
  @Input() contextType: any;
  @Input() newNotes: any;
  @Input() actorId: any;
  @Input() contextId: any;
  @Input() filters: boolean;
  @Input() queryType: 'or' | 'and' = 'and';
  @Input() readOnly: boolean = false;
  @Input() nonAberation: boolean = false;
  @Output() noteAddedEmit: EventEmitter<any> = new EventEmitter();

  public note: any = { };
  public noteLength: number = 0;
  public load: boolean = false;
  public notesFilter: string = 'all';
  public tooltipText: string = null;

  public get notes() {
    return this._notes.filter((e) => {
      return this.actorFilter(e);
    });
  }
  public set notes(value: any[]) {
    this._notes = value.sort((a, b) => {
      return DateHandler.getTime(new Date(a.meta.lastUpdated)) - DateHandler.getTime(new Date(b.meta.lastUpdated));
    });
  }

  private _notes: any[] = [];
  private destroyed: boolean = false;
  private DEFAULT_QUERY_TYPE: string = 'and';

  constructor(private coreAC: CoreActionCreatorService,
              private changeDetector: ChangeDetectorRef,
              private notesService: NotesService,
              private activityService: ActivityService) {

  }

  ngOnInit() {
    this.notesService.notePanelIsOpened = false;
    this.load = false;
    this.getNotes();
  }

  private getNotes(): void {
    let searchContext = this.contextId;
    let searchType = this.contextType;
    if (Array.isArray(this.contextId)) {
      searchContext = arrToQueryString(this.getQueryType(), this.contextId);
    }
    if (Array.isArray(searchType)) {
      searchType = arrToQueryString(this.getQueryType(), this.contextType);
    }

    let observable;
    observable = this.nonAberation ? this.notesService.get(searchContext, this.actorId) : this.notesService.getWithContextType(searchContext, searchType, this.actorId);
    observable.subscribe((res) => {
      if (this.destroyed) {
        return;
      }
      if (res) {
        this.notes = res.body;
      } else {
        return [];
      }
      this.setNotesRead();
      this.load = true;
      this.changeDetector.detectChanges();
      this.scrollToLastMessage();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contextId && !changes.contextId.firstChange) {
      this.getNotes();
    }
  }

  getQueryType() : string{
      return this.queryType || this.DEFAULT_QUERY_TYPE;
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  getAvatarImage(note): string {
    const photo = note.actor.photo;
    let avatarImageUrl = defaultAvatarUrl;
     /* The default avatar image used to be downloaded from a server in Pakistan, 
      this code portion was changed in order to avoid a huge update in DB for existing patients and 
      practitioners that have the previous defaults avatar.*/
    if (photo && photo[0] && !isEmpty(photo[0].url.trim())) {     
      if (!photo[0].url.contains('cdn.techjuice.pk')) { 
        avatarImageUrl = note.actor.photo[0].url; 
      }
    }
    return avatarImageUrl;
  }

  postNote() {
    if (this.noteLength > 0) {
      this.note.text = this.notesService.fixNoteText(this.note.text);
      const context = [];

      if (typeof this.contextId === 'string') {
        this.contextId = this.contextId.split(',');
      }

      if (typeof this.contextType === 'string') {
        this.contextType = this.contextType.split(',');
      }

      if (Array.isArray(this.contextId)) {
        for (let i = 0; i < this.contextId.length; i++) {
          context.push({
            reference: this.contextId[i],
            resourceType: this.contextType[i]
          });
        }
      }

      const note = {
          actor: {
            reference: this.actorId,
            resourceType: this.actorType
          },
          context: context,
          text: this.note.text,
          tooltipText: this.tooltipText,
          parentNote: null
        };
      this.coreAC.setLoader(true);
      this.notesService.save(note)
        .then((res) => {
          if (!this.nonAberation) {
            this.activityService.create({
              actor: {
                reference: res.body.actor.id,
                resourceType: res.body.actor.resourceType
              },
              context: context,
              action: ActivityActions.READ
            }).subscribe(() => {
              // Do Nothing
            });
          }

          if (this.nonAberation) {
            const activities = [];
            activities.push({
              actor: {
                reference: this.actorId,
                resourceType: note.actor.resourceType
              },
              context: [{
                reference: res.body.id,
                resourceType: 'Note'
              }],
              action: ActivityActions.READ
            });
            this.activityService.create(activities).subscribe(() => {
              // Do Nothing
            });
          }

          res.body.read = true;

          this.notes = this._notes.concat(res.body);
          this.note = { };
          this.changeDetector.detectChanges();
          this.noteAddedEmit.emit(res.body);
          this.scrollToLastMessage();
          this.coreAC.setLoader(false);
        })
        .catch(() => {
          this.coreAC.setLoader(false);
        });
    }
  }

  scrollToLastMessage() {
    const nodesList: HTMLElement = document.getElementById('notes-list');
    if (nodesList){
      nodesList.scrollTop = nodesList.scrollHeight;
    }
  }

  checkNote(event) {
    this.noteLength = event.textValue.trim().length;
    this.tooltipText = event.textValue;
  }

  actorFilter(item) {
    switch (this.notesFilter) {
      case 'my': {
        return item.actor.id == this.actorId;
      }
      case 'others': {
        return item.actor.id != this.actorId;
      }
      default: {
        return item;
      }
    }
  }

  setNotesRead() {
    const activities = [];
    this.notes.map((note) => {
      if (!note.read) {
        activities.push(
          {
            actor: {
              reference: this.actorId,
              resourceType: note.actor.resourceType
            },
            context: [{
              reference: note.id,
              resourceType: 'Note'
            }],
            action: ActivityActions.READ
          }
        );
      }
    });

    if (activities.length > 0) {
      this.activityService.create(activities)
        .subscribe(() => setTimeout(() => this.setNotesUIRead(), 30000));
    }
  }

  setNotesUIRead() {
    if (this.destroyed) {
      return;
    }
    this.notes.map((note) => {
      note.read = true;
    });
    this.changeDetector.detectChanges();
  }

  cssClassFormat(className: string) {
    return StringUtils.cssClassFormat(className);
  }
}
